import React from "react";
import { cn } from "../../utils/cn";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "outline" | "ghost";
  isLoading?: boolean;
  fullWidth?: boolean;
  color?: "primary" | "danger" | "success" | "warning" | "info";
  rounded?: "sm" | "md" | "lg" | "xl" | "full";
  size?: "sm" | "md" | "lg" | "xl";
}

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  isLoading,
  children,
  className,
  fullWidth = false,
  color = "primary",
  rounded = "lg",
  size = "md",
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 dark:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed";

  // Fully static per-color class strings — Tailwind's scanner can't see
  // classes assembled via `${colorMap[color]}` template interpolation, so
  // each color/variant combo is spelled out rather than composed at runtime.
  const colorVariants: Record<
    NonNullable<ButtonProps["color"]>,
    Record<NonNullable<ButtonProps["variant"]>, string>
  > = {
    primary: {
      primary:
        "bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500",
      outline:
        "border border-primary-300 text-primary-700 bg-white hover:bg-primary-50 focus:ring-primary-500 dark:border-primary-700 dark:text-primary-300 dark:bg-gray-800 dark:hover:bg-primary-900/30",
      ghost:
        "text-primary-600 hover:bg-primary-100 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-gray-700",
    },
    danger: {
      primary: "bg-red-600 hover:bg-red-700 text-white focus:ring-red-500",
      outline:
        "border border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500 dark:border-red-700 dark:text-red-400 dark:bg-gray-800 dark:hover:bg-red-900/30",
      ghost:
        "text-red-600 hover:bg-red-100 focus:ring-red-500 dark:text-red-400 dark:hover:bg-gray-700",
    },
    success: {
      primary:
        "bg-green-600 hover:bg-green-700 text-white focus:ring-green-500",
      outline:
        "border border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500 dark:border-green-700 dark:text-green-400 dark:bg-gray-800 dark:hover:bg-green-900/30",
      ghost:
        "text-green-600 hover:bg-green-100 focus:ring-green-500 dark:text-green-400 dark:hover:bg-gray-700",
    },
    warning: {
      primary:
        "bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500",
      outline:
        "border border-yellow-300 text-yellow-700 bg-white hover:bg-yellow-50 focus:ring-yellow-500 dark:border-yellow-700 dark:text-yellow-400 dark:bg-gray-800 dark:hover:bg-yellow-900/30",
      ghost:
        "text-yellow-600 hover:bg-yellow-100 focus:ring-yellow-500 dark:text-yellow-400 dark:hover:bg-gray-700",
    },
    info: {
      primary: "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500",
      outline:
        "border border-blue-300 text-blue-700 bg-white hover:bg-blue-50 focus:ring-blue-500 dark:border-blue-700 dark:text-blue-400 dark:bg-gray-800 dark:hover:bg-blue-900/30",
      ghost:
        "text-blue-600 hover:bg-blue-100 focus:ring-blue-500 dark:text-blue-400 dark:hover:bg-gray-700",
    },
  };

  const variants = colorVariants[color];

  const roundedMap = {
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    full: "rounded-full",
  };

  const sizeMap = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
    xl: "px-6 py-3 text-lg",
  };

  return (
    <button
      {...props}
      disabled={isLoading || props.disabled}
      className={cn(
        base,
        variants[variant],
        roundedMap[rounded],
        sizeMap[size],
        fullWidth ? "w-full" : "w-auto",
        className
      )}
    >
      {isLoading && (
        <svg
          className="animate-spin h-4 w-4 mr-2 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
          ></path>
        </svg>
      )}
      {children}
    </button>
  );
};

export default Button;
